/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Outlet, Link, NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../features/auth/hooks/useAuth'
import { Button } from '../shared/components/ui/button'
import { BrandLogo } from '../shared/components/brand/BrandLogo'
import { Avatar, AvatarFallback } from '../shared/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../shared/components/ui/dropdown-menu'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { cn } from '../shared/lib/utils'
import { MobileDrawer } from './MobileDrawer'
import { UserDrawer } from './UserDrawer'
import { useDrawerContext } from './DrawerContext'
import { Badge } from '../shared/components/ui/badge'
import { CountBadge } from '../shared/components/ui/count-badge'
import { SyncStatusDialog } from '../features/sync/components/SyncStatusDialog'
import { SearchPalette } from './SearchPalette'
import { SearchTrigger } from './SearchTrigger'
import { fetchNotifications } from '../shared/store/slices/notificationsSlice'
import { useNotificationPolling } from '../features/reports/hooks/useNotificationPolling'
import type { AppDispatch, RootState } from '../shared/store'
import { primaryNavItems, userMenuItems, visibleNavItems } from './navigation'
import { LogOutIcon, MenuIcon, RefreshCwIcon } from 'lucide-react'

/**
 * MainLayout component
 * - Desktop: persistent left sidebar (brand, search row, pill nav, account
 *   block); the unread-notification count rides on the account avatar
 * - Mobile: top bar with drawer navigation and job context; pages can hide it
 *   via useHideMobileHeader (DrawerContext)
 * - Global search is a ⌘K palette owned here, so it is reachable from every
 *   page rather than only the ones that opt into a header widget
 * - Sync status lives in a dialog opened from the account menu (desktop) or
 *   the nav drawer's sync row (mobile); a dot on the hamburger flags failures
 */
export const MainLayout = () => {
  const { user, role, logout } = useAuth()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [isUserDrawerOpen, setIsUserDrawerOpen] = useState(false)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const { jobContext, hideMobileHeader, mobileNavOpen, setMobileNavOpen } = useDrawerContext()
  const unreadCount = useSelector((state: RootState) => state.notifications.unreadCount)
  const syncFailedCount = useSelector((state: RootState) => state.sync.failedCount)

  // Single app-wide fetch — every badge and the /notifications page read the
  // store (the portal keeps its own fetch inside NotificationBell)
  useEffect(() => {
    void dispatch(fetchNotifications())
  }, [dispatch])
  // …and a 60 s refresh so a client's quote response shows within a minute (008 SC-006)
  useNotificationPolling()

  // Clients reach this layout for /profile and /settings, but have nothing to
  // search — no trigger and no shortcut for them
  const canSearch = role !== 'client'

  useEffect(() => {
    if (!canSearch) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setIsSearchOpen((open) => !open)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canSearch])

  const getInitials = (name: string | undefined) => {
    if (!name) return '?'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const handleLogout = async () => {
    await logout()
    void navigate('/login')
  }

  const visibleLinks = visibleNavItems(primaryNavItems, role)
  const visibleUserMenuItems = visibleNavItems(userMenuItems, role)

  const accountMenuContent = (
    <>
      <DropdownMenuLabel>
        <div className="flex items-center space-x-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback>{getInitials(user?.user_metadata?.full_name)}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {user?.user_metadata?.full_name || 'User'}
            </p>
            <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
            {role && (
              <p className="text-xs leading-none text-muted-foreground capitalize">Role: {role}</p>
            )}
          </div>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {visibleUserMenuItems.map((item) => (
        <DropdownMenuItem asChild key={item.to}>
          <Link to={item.to}>
            {item.icon && <item.icon className="mr-2 h-4 w-4" />}
            {item.label}
            {item.showsUnreadBadge && unreadCount > 0 && (
              <Badge variant="destructive" className="ml-auto">
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Link>
        </DropdownMenuItem>
      ))}
      <DropdownMenuItem onSelect={() => setIsSyncDialogOpen(true)}>
        <RefreshCwIcon className="mr-2 h-4 w-4" />
        Sync Status
        {syncFailedCount > 0 && (
          <Badge variant="destructive" className="ml-auto">
            {syncFailedCount}
          </Badge>
        )}
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild>
        <button onClick={() => void handleLogout()} className="w-full text-destructive">
          <LogOutIcon className="mr-2 h-4 w-4" />
          Logout
        </button>
      </DropdownMenuItem>
    </>
  )

  return (
    <div className="min-h-screen bg-background md:flex">
      {/* Mobile Navigation Drawer (left) */}
      <MobileDrawer
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        navLinks={visibleLinks}
        jobContext={jobContext || undefined}
        onOpenSyncStatus={() => setIsSyncDialogOpen(true)}
        onOpenSearch={canSearch ? () => setIsSearchOpen(true) : undefined}
      />

      {/* Mobile User Drawer (right) */}
      <UserDrawer isOpen={isUserDrawerOpen} onClose={() => setIsUserDrawerOpen(false)} />

      {/* Always mounted (closed): its hook owns the online/offline listeners
          and the initial queue-stats fetch */}
      <SyncStatusDialog open={isSyncDialogOpen} onOpenChange={setIsSyncDialogOpen} />

      {/* Global search — mounted here (not per page) so ⌘K works everywhere.
          Its body is lazy, so this costs nothing until first open */}
      <SearchPalette
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        userId={user?.id}
        role={role}
        onOpenSyncStatus={() => setIsSyncDialogOpen(true)}
      />

      {/* Desktop sidebar */}
      {/* high-contrast:border-r-2 — the white sidebar needs an ink edge when
          surfaces stop carrying fills */}
      <aside className="hidden flex-col bg-sidebar text-sidebar-foreground px-4 py-6 high-contrast:border-r-2 md:sticky md:top-0 md:flex md:h-screen md:w-[252px] md:shrink-0">
        <Link to="/dashboard" className="self-start px-3">
          <BrandLogo className="h-8 w-auto" />
        </Link>

        {/* Search sits between the brand and the nav links: a fixed offset
            from the top (unlike the bottom-pinned account block), and
            bordered rather than pill-filled so it doesn't read as a
            navigation destination */}
        {canSearch && (
          <SearchTrigger onClick={() => setIsSearchOpen(true)} showShortcut className="mt-6 h-11" />
        )}

        <nav className={cn('flex flex-col gap-1', canSearch ? 'mt-6' : 'mt-8')}>
          {visibleLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'flex h-[46px] items-center gap-3 rounded-full px-4 text-[15px] font-medium transition-colors',
                  isActive
                    ? 'bg-sidebar-primary text-sidebar-primary-foreground shadow-sm'
                    : 'text-sidebar-foreground/80 hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]'
                )
              }
            >
              {link.icon && <link.icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />}
              {link.label}
            </NavLink>
          ))}
        </nav>

        {/* Account block pinned at the bottom — the avatar carries the
            unread-notification count */}
        <div className="mt-auto">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full cursor-pointer items-center gap-3 rounded-full p-2 pr-4 text-left transition-colors hover:bg-[color-mix(in_srgb,currentColor_7%,transparent)]"
              >
                <span className="relative shrink-0">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{getInitials(user?.user_metadata?.full_name)}</AvatarFallback>
                  </Avatar>
                  <CountBadge count={unreadCount} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">
                    {user?.user_metadata?.full_name || 'User'}
                  </span>
                  {role && (
                    <span className="block text-xs capitalize text-muted-foreground">{role}</span>
                  )}
                  {unreadCount > 0 && (
                    <span className="sr-only">, {unreadCount} unread notifications</span>
                  )}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-56">
              {accountMenuContent}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <header
          className={cn(
            'sticky top-0 z-50 w-full border-b bg-background md:hidden',
            hideMobileHeader && 'hidden'
          )}
        >
          {/* 60px bar around the 48px (h-12) icon targets */}
          <div className="flex h-[60px] items-center px-2">
            <Button
              variant="ghost"
              size="icon"
              className="relative"
              aria-label={
                syncFailedCount > 0 ? 'Open navigation, sync needs attention' : 'Open navigation'
              }
              onClick={() => setMobileNavOpen(true)}
            >
              <MenuIcon className="h-5 w-5" />
              {/* Failure-only ambient signal — sync detail lives on the
                  drawer's sync row */}
              {syncFailedCount > 0 && (
                <span
                  aria-hidden="true"
                  className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive"
                />
              )}
            </Button>

            {/* The GPS readout now lives in JobDetail's floating pill — this
                header is hidden on that screen anyway */}
            <Link to="/dashboard" className="ml-1">
              <BrandLogo className="h-7 w-auto" />
            </Link>
            <div className="flex-1" />

            <Button
              variant="ghost"
              size="icon"
              aria-label={
                unreadCount > 0
                  ? `Open account menu, ${unreadCount > 9 ? '9+' : unreadCount} unread notifications`
                  : 'Open account menu'
              }
              onClick={() => setIsUserDrawerOpen(true)}
            >
              {/* h-9: with the bell gone the avatar is the notification
                  surface — a touch larger so the count badge sits clear */}
              <span className="relative">
                <Avatar className="h-9 w-9">
                  <AvatarFallback>{getInitials(user?.user_metadata?.full_name)}</AvatarFallback>
                </Avatar>
                <CountBadge count={unreadCount} />
              </span>
            </Button>
          </div>
        </header>

        {/* Main content — 18px gutters mobile, 32px desktop. Desktop keeps
            the 92px lead (the former 68px tools strip + 24px page padding);
            search lives in the sidebar, notifications on the account avatar.
            JobDetail's viewport lock is measured off this padding — see
            CLAUDE.md before changing it */}
        <main className="w-full max-w-[1240px] flex-1 px-[18px] py-6 md:px-8 md:pt-[92px]">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
